
import React from "react";
import { Trophy, TrendingUp, ArrowDownRight, Award, BarChart3, LineChart, DollarSign } from "lucide-react";

interface StrategyStatsProps {
  riskLevel?: number;
  winRate?: number | null;
  maxDrawdown?: number | null;
  tradesWon?: number | null;
  tradesLost?: number | null;
  aum?: number | null;
  pnlRatio?: number | null;
  timespan?: number | null;
  isPro?: boolean;
  infoIconColor?: string;
}

export const useStrategyStats = ({ 
  riskLevel, 
  winRate, 
  maxDrawdown, 
  tradesWon, 
  tradesLost, 
  aum, 
  isPro = false,
  pnlRatio,
  timespan,
  infoIconColor
}: StrategyStatsProps) => {
  let stats = [];

  // Performance stat - only add if we have a valid pnlRatio
  if (pnlRatio !== null && pnlRatio !== undefined) {
    stats.push({
      label: timespan ? `${timespan} days performance` : 'Performance',
      value: `${pnlRatio.toFixed(2)}%`,
      icon: <TrendingUp className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "text-green-500",
      tooltip: "The performance over the specified time period.",
      infoIconColor
    });
  }

  // Win Rate stat
  if (winRate !== null && winRate !== undefined) {
    stats.push({
      label: "Win Rate",
      value: `${winRate.toFixed(0)}%`,
      icon: <Trophy className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "",
      tooltip: "Shows the average win rate of a Master Trader over a certain period.",
      infoIconColor
    });
  }

  // Max Drawdown stat
  if (maxDrawdown !== null && maxDrawdown !== undefined) {
    stats.push({
      label: "MDD",
      value: `${maxDrawdown.toFixed(1)}%`,
      icon: <ArrowDownRight className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "",
      tooltip: "A low Maximum Drawdown indicates that the unrealized losses from a Master Trader's trading strategy has been relatively small.",
      infoIconColor
    });
  }

  // Trades Won stat
  if (tradesWon !== null && tradesWon !== undefined) {
    stats.push({
      label: "Trades Won",
      value: `${tradesWon}`,
      icon: <Award className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "",
      infoIconColor
    });
  }

  // Trades Lost stat
  if (tradesLost !== null && tradesLost !== undefined) {
    stats.push({
      label: "Trades Lost",
      value: `${tradesLost}`,
      icon: <BarChart3 className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "",
      infoIconColor
    });
  }

  // AUM stat
  if (aum !== null && aum !== undefined) {
    stats.push({
      label: "AUM",
      value: `$${aum.toLocaleString()}`,
      icon: <DollarSign className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "",
      tooltip: "Assets under management",
      infoIconColor
    });
  }

  // Risk Level stat (only if no other stats are available)
  if (riskLevel !== undefined && stats.length === 0) {
    const riskLevelLabel = riskLevel === 1 ? "Low" : riskLevel === 2 ? "Medium" : "High";
    const riskLevelColor = riskLevel === 1 ? "text-green-500" : riskLevel === 2 ? "text-yellow-500" : "text-red-500";
    
    stats.push({
      label: "Risk Level",
      value: riskLevelLabel,
      icon: <LineChart className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : riskLevelColor,
      infoIconColor
    });
  }

  // Win/Lose Ratio (only if win rate is not available)
  if (!winRate && tradesWon !== null && tradesLost !== null && tradesWon !== undefined && tradesLost !== undefined) {
    const ratio = (tradesWon / (tradesWon + tradesLost)) * 100;
    stats.push({
      label: "Success Rate",
      value: `${ratio.toFixed(0)}%`,
      icon: <Trophy className="w-4 h-4 text-white" />,
      valueColor: isPro ? "text-[#F6C528]" : "",
      tooltip: "The percentage of successful trades relative to the total number of trades.",
      infoIconColor
    });
  }

  return { stats };
};
